/**
 * BCP Actions Hook
 * Centralizes save, export, and regenerate operations
 */

import { useCallback } from 'react';
import type { BCPFormData } from '../types';
import type { BCPType, BCPSaveRequest, BCPUpdateRequest, BCPDocument } from '../types/bcp';
import type { GeneratedBCPMetadata, UploadData } from '../types/bcp-generation';
import { bcpService } from '../services/bcpService';
import { exportBCP } from '../services/api';
import { useLoadingState } from './useLoadingState';
import { useAutoDismissMessage } from './useAutoDismissMessage';
import { sanitizeObject } from '../utils/textSanitizer';

export interface BCPActionsOptions {
  isEditMode: boolean;
  bcpDocument?: BCPDocument | null;
  bcpType: BCPType;
  tenantId: string | null;
  apiResponse?: UploadData;
  onRegenerate?: () => void;
}

export interface BCPActionsState {
  // Loading States
  saveLoading: boolean;
  exportLoading: boolean;
  
  // Actions
  onSave: (formData: BCPFormData) => Promise<string | void>;
  onExport: (formData: BCPFormData, format: 'pdf' | 'docx') => Promise<void>;
  onRegenerate: () => void;
  
  // Status
  saveError: string | null;
  exportError: string | null;
  successMessage: string | null;
}

export const useBCPActions = ({
  isEditMode,
  bcpDocument,
  bcpType,
  tenantId,
  apiResponse,
  onRegenerate
}: BCPActionsOptions): BCPActionsState => {
  const saveState = useLoadingState<string>();
  const exportState = useLoadingState<Blob>();
  const { message: successMessage, showMessage: setSuccessMessage } = useAutoDismissMessage();
  
  // Save BCP (create or update)
  const handleSave = useCallback(async (formData: BCPFormData): Promise<string | void> => {
    return await saveState.execute(async () => {
      try {
        // Sanitize form data before saving
        const sanitizedFormData = sanitizeObject(formData);

        if (isEditMode && bcpDocument) {
          // Update existing BCP
          const updateRequest: BCPUpdateRequest = {
            content: {
              title: bcpDocument.content.title || `${bcpType}版BCP_${new Date().toLocaleDateString('ja-JP')}`,
              sections: sanitizedFormData
            },
            form_data: sanitizedFormData,
            status: 'published',
            tags: bcpDocument.tags || ['generated', bcpType]
          };

          const updatedBCP = await bcpService.updateBCP(bcpDocument.id, updateRequest);
          setSuccessMessage('保存が完了しました。');
          return updatedBCP.id;
        } else {
          // Create new BCP
          const saveRequest: BCPSaveRequest = {
            type: bcpType,
            content: {
              title: `${bcpType}版BCP_${new Date().toLocaleDateString('ja-JP')}`,
              sections: sanitizedFormData
            },
            form_data: sanitizedFormData,
            generation_metadata: apiResponse?.generation_metadata,
            status: 'published',
            category: 'generated',
            tags: ['generated', bcpType]
          };

          const savedBCP = await bcpService.saveBCP(saveRequest, tenantId || 'default');
          setSuccessMessage(`保存が完了しました。`);
          return savedBCP.id;
        }
      } catch (error) {
        throw new Error('BCPの保存に失敗しました。');
      }
    });
  }, [
    isEditMode,
    bcpDocument,
    bcpType,
    tenantId,
    apiResponse?.generation_metadata,
    saveState,
    setSuccessMessage
  ]);
  
  // Export BCP
  const handleExport = useCallback(async (
    formData: BCPFormData,
    format: 'pdf' | 'docx'
  ): Promise<void> => {
    await exportState.execute(async () => {
      // Sanitize form data before exporting
      const sanitizedFormData = sanitizeObject(formData);
      const response = await exportBCP(sanitizedFormData, format);

      if (response.success) {
        setSuccessMessage(`BCP${format.toUpperCase()}ファイルのダウンロードを開始しました。`);
        return new Blob(); // Actual blob would be returned from response.data
      }
      throw new Error('Export failed');
    });
  }, [exportState, setSuccessMessage]);
  
  // Regenerate BCP
  const handleRegenerate = useCallback(() => {
    if (onRegenerate) {
      onRegenerate();
    }
  }, [onRegenerate]);
  
  return {
    // Loading States
    saveLoading: saveState.isLoading,
    exportLoading: exportState.isLoading,
    
    // Actions
    onSave: handleSave,
    onExport: handleExport,
    onRegenerate: handleRegenerate,
    
    // Status
    saveError: saveState.error,
    exportError: exportState.error,
    successMessage,
  };
};
